/* global instantsearch */

import TypesenseInstantSearchAdapter from "typesense-instantsearch-adapter";

import { API_BASE, TYPESENSE_SERVER } from "./config";

const modalElement = document.querySelector("#record-modal");
const modalContentElement = document.querySelector("#record-modal-content");
const hitsElement = document.querySelector("#hits");
const searchPreviewShell = document.querySelector("#search-preview-shell");
const gridViewButton = document.querySelector("#search-view-grid");
const listViewButton = document.querySelector("#search-view-list");

function formatDate(timestamp) {
  if (!timestamp) {
    return "Not published";
  }
  return new Date(timestamp * 1000).toISOString().slice(0, 10);
}

function highlightValue(value, fallback) {
  return value?.value || fallback || "";
}

function renderTags(tags) {
  return (tags || []).map((tag) => `<span>${tag}</span>`).join("");
}

function buildCover(item) {
  if (item.cover_image_url) {
    return `
      <div class="record-card__cover has-image" style="background-image: url('${item.cover_image_url}')">
        <div class="record-card__cover-badge">${item.category}</div>
      </div>
    `;
  }

  const initials = String(item.name || "R")
    .split(/\s+/)
    .slice(0, 2)
    .map((chunk) => chunk[0] || "")
    .join("")
    .toUpperCase();

  return `
    <div class="record-card__cover">
      <div class="record-card__initials">${initials}</div>
      <div class="record-card__cover-badge">${item.category}</div>
    </div>
  `;
}

function setModalVisibility(visible) {
  modalElement.hidden = !visible;
  document.body.style.overflow = visible ? "hidden" : "";
}

function workspaceUrl(recordId) {
  return `/?recordId=${encodeURIComponent(recordId)}&tab=editor#records`;
}

function setSearchView(view) {
  if (!searchPreviewShell) {
    return;
  }

  const isList = view === "list";
  searchPreviewShell.classList.toggle("search-layout--list", isList);
  gridViewButton?.classList.toggle("is-active", !isList);
  gridViewButton?.classList.toggle("button--secondary", !isList);
  gridViewButton?.classList.toggle("button--ghost", isList);
  listViewButton?.classList.toggle("is-active", isList);
  listViewButton?.classList.toggle("button--secondary", isList);
  listViewButton?.classList.toggle("button--ghost", !isList);
}

async function openRecordModal(recordId) {
  const response = await fetch(`${API_BASE}/records/${recordId}`);
  const record = await response.json();

  if (!response.ok) {
    throw new Error(record.error || "Unable to load record");
  }

  modalContentElement.innerHTML = `
    <article class="detail-view">
      ${
        record.coverImageUrl
          ? `<div class="detail-view__hero" style="background-image: url('${record.coverImageUrl}')"></div>`
          : `<div class="detail-view__hero"></div>`
      }
      <div class="detail-view__heading">
        <div class="record-card__meta">
          <span>${record.category}</span>
          <span>${record.country}</span>
          <span>${record.region}</span>
          <span>${record.source}</span>
        </div>
        <h2>${record.name}</h2>
      </div>
      <div class="record-card__tags">${renderTags(record.tags)}</div>
      <div class="record-card__footer">
        <span>Status: ${record.status}</span>
        <span>Updated: ${formatDate(record.updatedAt)}</span>
        <span>Score: ${record.score}</span>
      </div>
      <div class="detail-view__content">
        ${record.contentHtml || `<p>${record.descriptionPlain}</p>`}
      </div>
      <div class="record-card__actions">
        <a class="button button--secondary" href="${workspaceUrl(record.id)}">Open in records</a>
        ${
          record.externalUrl
            ? `<a class="button button--primary" href="${record.externalUrl}" target="_blank" rel="noreferrer">Open external link</a>`
            : ""
        }
      </div>
    </article>
  `;

  setModalVisibility(true);
}

document.addEventListener("click", (event) => {
  const detailTrigger = event.target.closest("[data-record-id]");
  if (detailTrigger) {
    openRecordModal(detailTrigger.dataset.recordId).catch((error) => {
      console.error(error);
      window.alert(error.message);
    });
  }

  if (event.target.closest("[data-close-modal]")) {
    setModalVisibility(false);
  }
});

document.addEventListener("keydown", (event) => {
  if (event.key === "Escape" && !modalElement.hidden) {
    setModalVisibility(false);
  }
});

const typesenseInstantsearchAdapter = new TypesenseInstantSearchAdapter({
  server: TYPESENSE_SERVER,
  additionalSearchParameters: {
    query_by:
      "name,category,country,region,tags,source,description_plain,content_plain",
  },
});

const search = instantsearch({
  searchClient: typesenseInstantsearchAdapter.searchClient,
  indexName: "records",
});

search.addWidgets([
  instantsearch.widgets.searchBox({
    container: "#searchbox",
    placeholder: "Search names, regions, tags, countries, or source",
    showReset: true,
    showSubmit: true,
  }),
  instantsearch.widgets.configure({
    hitsPerPage: 6,
  }),
  instantsearch.widgets.stats({
    container: "#stats",
  }),
  instantsearch.widgets.clearRefinements({
    container: "#clear-refinements",
    templates: {
      resetLabel() {
        return "Clear all filters";
      },
    },
  }),
  instantsearch.widgets.refinementList({
    container: "#category-list",
    attribute: "category",
    sortBy: ["name:asc"],
  }),
  instantsearch.widgets.refinementList({
    container: "#country-list",
    attribute: "country",
    sortBy: ["name:asc"],
  }),
  instantsearch.widgets.refinementList({
    container: "#tags-list",
    attribute: "tags",
    sortBy: ["name:asc"],
  }),
  instantsearch.widgets.refinementList({
    container: "#source-list",
    attribute: "source",
    sortBy: ["name:asc"],
  }),
  instantsearch.widgets.hits({
    container: "#hits",
    templates: {
      empty(results) {
        return `
          <div class="card">
            <div class="panel-heading">
              <span>No published records found</span>
            </div>
            <p>Try a different keyword or remove some filters. Query: <strong>${results.query}</strong></p>
          </div>
        `;
      },
      item(item) {
        return `
        <article class="record-card card">
            ${buildCover(item)}
            <div class="record-card__body">
              <h2 class="record-card__title">${highlightValue(item._highlightResult?.name, item.name)}</h2>
              <div class="record-card__meta">
                <span>${item.country}</span>
                <span>${item.region}</span>
              </div>
              <p class="record-card__description">${item.description_plain || "No summary available."}</p>
              <div class="record-card__tags">${renderTags(item.tags)}</div>
              <div class="record-card__footer">
                <span>Source: ${item.source}</span>
                <span>Updated: ${formatDate(item.updated_at)}</span>
                <span>Score: ${item.score}</span>
              </div>
              <div class="record-card__actions">
                <small>Published record</small>
                <div class="record-card__action-group">
                  <a class="button button--secondary button--small" href="${workspaceUrl(item.id)}">Edit record</a>
                  <button class="button button--ghost button--small" type="button" data-record-id="${item.id}">
                    Open details
                  </button>
                </div>
              </div>
            </div>
          </article>
        `;
      },
    },
  }),
  instantsearch.widgets.pagination({
    container: "#pagination",
  }),
]);

search.start();

gridViewButton?.addEventListener("click", () => setSearchView("grid"));
listViewButton?.addEventListener("click", () => setSearchView("list"));
setSearchView("grid");
window.addEventListener("records:changed", () => {
  search.refresh();
});
window.addEventListener("workspace:tab-changed", (event) => {
  if (event.detail?.view === "records" && event.detail?.tab === "preview") {
    search.refresh();
  }
});

hitsElement.addEventListener("keydown", (event) => {
  if (event.key === "Enter") {
    const button = event.target.closest("[data-record-id]");
    if (button) {
      button.click();
    }
  }
});
